
import { trackEvent } from '@@/bits/analytics'
import { navigateTo } from '@@/bits/location'
import { PiniaMapState } from '@@/bits/pinia'
import { isTweet } from '@@/bits/post_attachment'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { LinkHelpers } from '@padlet/beethoven-client'
import type { BeethovenContentCategory, BeethovenContentSubcategory } from '@padlet/beethoven-client/lib/types'
import appCan from 'bits/app_can'
import {
  FallbackAttachmentType,
  downloadUrl,
  fallbackImageUrl,
  getStylizedProviderName,
  isAttachmentBlocked,
  urlForOpening,
} from 'bits/attachments'
import device from 'bits/device'
import { observeIntersection } from 'bits/intersection'
import type { RGB } from 'bits/post_color'
import { computeLuminanceRatio, postColorRgb, serializeRgbaStringToRgba } from 'bits/post_color'
import { addQueryParams, getHostname, NATIVE_HOST } from 'bits/url'
import { Component, Getter, Prop, State, Vue, Watch, defineAsyncComponent, namespace } from 'bits/vue'
import { isAttachmentSupportedByZoom, openUrlInBrowserFromZoom } from 'bits/zoom'
import { isEqual } from 'lodash-es'
import { attachmentCaptionForDisplay } from 'surface/attachment_caption_for_display'
import type { Cid, Post } from 'types'
import SurfacePostAttachmentButton from 'vuecomponents/SurfacePostAttachmentButton.vue'
import BeethovenAttachmentCaption from 'vuecomponents/beethoven-attachment-caption.vue'
import BeethovenAttachmentPreview from 'vuecomponents/beethoven-attachment-preview.vue'
import emitClickOnTouch from 'vuedirectives/emit_click_on_touch'
import { checkIfUserIsInZoomCollaborateMode } from '@@/bits/zoom'

@Component({
  components: {
    BeethovenAttachmentPreview,
    BeethovenAttachmentCaption,
    SurfacePostAttachmentButton,
    SurfaceAttachmentContentBlocked: defineAsyncComponent(
      () => import('vuecomponents/SurfaceAttachmentContentBlocked.vue'),
    ),
  },
  directives: {
    emitClickOnTouch,
  },
})
export default class SurfacePostAttachmentNew extends Vue {
  $el!: HTMLDivElement
  $refs!: {
    embedWrapper: HTMLDivElement
    attachmentCaption: BeethovenAttachmentCaption
  }

  @State isUserHostingZoomCollaboration!: boolean
  @Prop() post!: Post
  @Prop({ required: true }) displayedPostWidth!: number
  @Prop() isEditableByCurrentUser!: boolean
  @Prop() isWishContentScrollable!: boolean
  @Prop({ default: true }) actionable!: boolean
  @Getter isStream!: boolean
  @Getter format!: string
  @Getter colorScheme!: 'light' | 'dark'
  @Getter isAPostDragging!: boolean
  @State isSafari!: boolean
  @State isIOS!: boolean
  @Getter blockedContentProviders!: Set<string>
  @PiniaMapState(useSurfacePostsStore) canIDragPosts!: boolean
  @namespace('browser').Getter windowWidth!: number
  @namespace('browser').Getter isSmallerThanDesktop!: boolean
  @namespace('attachment').Action setSurfacePreviewImageLoaded!: (payload: { url: string }) => void

  isImageLoaded = false
  isError = false
  hasBlockedContent = false
  linkData: BeethovenDisplayAttributes | null = null
  isFallback = false
  showMeta = false

  contentCategory: BeethovenContentCategory | null = null
  contentSubcategory: BeethovenContentSubcategory | null = null
  downloadFileName: string | null = null
  attachmentAspectRatio = 0
  attachmentHeight = 0
  attachmentColor = 'rgb(0,0,0)'
  providerName = ''
  lowercaseProviderName = ''
  attachmentLuminance: any
  isDownloadable = false
  attachmentEmbedCode = ''
  errorText: string | null = null

  @State isMobile!: boolean

  public postMessage(event: string): void {
    switch (event) {
      case 'back-to-preview':
        this.backToPreview()
        return
      case 'zoom':
        this.zoom()
        return
      case 'open-in-new':
        this.backToPreview()
        this.openInNew()
    }
  }

  handleBeethovenError(error): void {
    this.isError = true
    this.errorText = error
  }

  handleDataLoaded(data): void {
    this.errorText = null
    this.isError = false
    this.hasBlockedContent = isAttachmentBlocked(data, this.blockedContentProviders)
    this.linkData = data
    this.isFallback = data.is_fallback
    this.contentCategory = data.content_category
    this.contentSubcategory = data.content_subcategory
    this.attachmentEmbedCode = data.embed_code
    this.attachmentAspectRatio = data.image_aspect_ratio
    this.attachmentHeight = data.image_height
    this.attachmentColor = data.image_color
    this.attachmentLuminance = data.image_luminance
    this.isDownloadable = data.is_downloadable && !this.isMobile
    this.showMeta = data.show_meta
    this.providerName = getStylizedProviderName(data.provider_name)
    this.lowercaseProviderName = data.provider_name?.toLowerCase() ?? ''

    if (this.contentCategory === 'file') {
      this.downloadFileName = this.post.subject || null
    }

    this.$emit('load-data', data)
  }

  handleImageLoaded(data): void {
    this.isImageLoaded = true
    if (this.url) {
      this.setSurfacePreviewImageLoaded({ url: this.url })
    }
    this.$emit('load-image', data)
  }

  get ariaLabel(): string {
    switch (this.interaction) {
      case 'play':
        return this.__('Play media')
      case 'download':
        return this.__('Download file')
      case 'link':
        return this.__('Open link')
      case 'no-action':
        return this.__('Post attachment')
      case 'zoom':
      case 'expand':
      default:
        return this.__('Open post attachment')
    }
  }

  get isDataLoaded(): boolean {
    // We show the loading placeholder until we have `linkData` emitted from `beethoven-attachment-preview`.
    // For tweets, we don't rely on this `linkData` but instead we use `post.wish_content.attachment_props`.
    // See the comment in `beethoven-attachment-preview.vue`'s `getBeethovenAttributes` method for more details.
    return (!this.isError && !!this.linkData) || isTweet(this.post.wish_content?.attachment_props)
  }

  get downloadUrl(): string {
    return downloadUrl(this.urlForDisplay as string)
  }

  get urlForOpening(): string {
    // If attachment should be opened in expanded view, we set post permalink
    // as the URL so that cmd/ctrl clicking opens it in a new tab.
    if (this.interaction === 'expand' && this.post.permalink) {
      return this.post.permalink
    }
    return urlForOpening(this.linkData, this.urlForDisplay)
  }

  get attachmentCaption(): string {
    return attachmentCaptionForDisplay({
      attachmentCaption: this.post?.attachment_caption,
      attachmentDisplayAttributes: this.linkData,
    })
  }

  get hasCaption(): boolean {
    return !!this.attachmentCaption
  }

  get url(): string | undefined {
    return this.post?.attachment
  }

  @Watch('url')
  handleUrlChanged(): void {
    this.isError = false
    this.isImageLoaded = false
    this.linkData = null
    this.isFallback = false
    this.showMeta = false
  }

  get urlForDisplay(): string | undefined {
    // Note: This should be used in display/href/... instead of this.url so if the attachment is censored (e.g. unsafe links), the bad attachment won't be exposed
    if (isTweet(this.post.wish_content?.attachment_props)) return this.post.wish_content?.attachment_props?.url
    if (this.linkData?.url) return this.linkData?.url
    if (this.isError) return this.url
    return undefined
  }

  get backgroundColor(): RGB {
    return postColorRgb({ postColor: this.post.color, isLightColorScheme: this.isLightColorScheme })
  }

  @Watch('backgroundColor')
  handlePostColorChange(newVal, oldVal) {
    // Dec 6 2022: We added this deep comparison check to prevent calling `embedInline` if the RGB
    // values haven't changed. The watcher can be triggered when we toggle the settings panel, which
    // is a weird bug because none of `backgroundColor` dependencies change (`this.post.color` and `this.isLightColorScheme`)
    // but the composed object does change (different reference, same value). That's why we need a deep comparison.
    // Additional note: the getter value is only recomputed when we add the watcher.
    const bgColorHasChanged = !isEqual(newVal, oldVal)
    if (bgColorHasChanged && this.showEmbed && !!this.linkData && LinkHelpers.isAudio(this.linkData)) {
      this.embedInline()
    }
  }

  get imageBackgroundColor(): RGB {
    if (!this.attachmentColor) return this.backgroundColor
    return serializeRgbaStringToRgba(this.attachmentColor)
  }

  get imageBackgroundColorString() {
    const c = this.imageBackgroundColor
    return `rgb(${c.r},${c.g},${c.b})`
  }

  get isShowingAttachmentBorder(): boolean {
    const luminanceRatio = computeLuminanceRatio(this.backgroundColor, this.imageBackgroundColor)
    return luminanceRatio < 1.5
  }

  get isLightColorScheme(): boolean {
    return this.colorScheme === 'light'
  }

  get placeholderColorClass(): string {
    if (this.isDataLoaded) {
      return ''
    }

    // The pulse animation can sometimes be expensive in terms of CPU usage and style recalcs per second according to Google Chrome's performance monitor
    // => only show it when the attachment is visible inside the viewport
    if (!this.hasIntersectedViewport) {
      return ''
    }

    // pulse between post background color and 3 shade darker in light mode
    // pulse between post background color and 2 shade lighter in dark mode
    switch (this.post.color) {
      case 'red':
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-scarlet-100 pulse-bg-to-scarlet-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-scarlet-900 pulse-bg-to-scarlet-700'
      case 'orange':
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-canary-100 pulse-bg-to-canary-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-tangerine-900 pulse-bg-to-tangerine-700'
      case 'green':
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-park-100 pulse-bg-to-park-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-park-900 pulse-bg-to-park-700'
      case 'blue':
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-oceanic-100 pulse-bg-to-oceanic-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-oceanic-900 pulse-bg-to-oceanic-700'
      case 'purple':
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-grape-100 pulse-bg-to-grape-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-grape-900 pulse-bg-to-grape-700'
      default:
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-light-ui-100 pulse-bg-to-light-ui-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-dark-ui-100 pulse-bg-to-dark-ui-400'
    }
  }

  get attachmentWidth(): number {
    return this.isStream ? this.displayedPostWidth - 24 : this.displayedPostWidth - 16
  }

  get realAttachmentHeight(): number {
    return this.attachmentHeight || this.attachmentWidth / this.attachmentAspectRatio
  }

  get isPadletAttachment(): boolean {
    // provider_name for a wall is the builder's name, "padlet" should be used only for landside pages (e.g. padlet.com, padlet.com/features)
    return this.contentSubcategory === 'padlet' && this.providerName.toLowerCase() !== 'padlet'
  }

  get interaction(): 'no-action' | 'play' | 'zoom' | 'download' | 'link' | 'expand' | 'padlet' {
    if (isTweet(this.linkData?.metadata?.attachment_props)) {
      // Ensure we never show the Tweet embed when using the new Tweet preview,
      // instead, we take the users to the tweet on Twitter.
      return 'link'
    }

    // Return link if it is a zoom app and it is a non-youtube video

    if (device.zoomApp && this.contentSubcategory === 'padlet') {
      return 'padlet'
    }

    if (this.isUnsupportedAttachmentOnZoom()) {
      return 'link'
    }

    if (!this.actionable) {
      return 'no-action'
    }
    if (this.contentCategory === 'audio' && this.providerName.toLowerCase() !== 'padlet drive' && !this.isStream) {
      return 'expand'
    }
    if (this.providerName.toLowerCase() === 'tiktok') {
      return 'expand'
    }
    if (this.contentCategory === 'video' || this.contentCategory === 'audio') {
      return 'play'
    }
    if (this.isDownloadable && this.contentCategory === 'file') {
      return 'download'
    }
    if (this.contentCategory === 'photo') {
      return 'expand'
    }
    if (this.contentCategory === 'page') {
      return 'link'
    }
    if (this.attachmentEmbedCode && this.contentSubcategory !== 'padlet') {
      return 'expand'
    }

    return 'link'
  }

  get buttonIcon(): string | null {
    if (this.isUnsupportedAttachmentOnZoom()) return null
    if (this.contentCategory === 'video' || this.contentCategory === 'audio') {
      return 'play'
    }
    return null
  }

  @namespace('post').Action startEditingPost!: (payload: {
    postCid: Cid
    shouldFocusAttachmentCaption: boolean
  }) => void

  openPostModal(): void {
    if (this.isEditableByCurrentUser) {
      this.startEditingPost({ postCid: this.post.cid, shouldFocusAttachmentCaption: true })
    }
  }

  // * EMBEDS AND ACTIONS

  embedHtml: string | null = null

  @namespace('postAction').State currentlyPlayingPostCid!: Cid
  @namespace('postAction').Action expandPost!: (payload: { postCid: Cid; xTextPanel?: boolean }) => void
  @namespace('postAction').Action zoomPost!: (payload: {
    zoomedPostCid: Cid
    zoomedLink: BeethovenDisplayAttributes
  }) => void

  @namespace('postAction').Action setCurrentlyPlayingPost!: (payload: { cid: Cid }) => void
  @namespace('postAction').Action unsetCurrentlyPlayingPost!: () => void

  get showEmbed(): boolean {
    return this.currentlyPlayingPostCid === this.post.cid
  }

  @Watch('showEmbed')
  toggleEmbedControl(xEmbed): void {
    this.$emit('toggle-embed-control', {
      xEmbed,
      isDownloadable: this.isDownloadable,
      downloadUrl: this.downloadUrl,
    })
  }

  @Watch('currentlyPlayingPostCid')
  onCurrentlyPlayingPostCidChannge(newCid: Cid): void {
    if (this.showEmbed && newCid && this.post.cid !== newCid) this.backToPreview()
  }

  embedInline(): void {
    this.setCurrentlyPlayingPost({ cid: this.post.cid })
    if (this.hasBlockedContent) return
    this.embedHtml = this.attachmentEmbedCode
    this.$nextTick(() => {
      const embedTag = this.$refs.embedWrapper.firstChild as Element
      if (embedTag.tagName.toLowerCase() === 'iframe') {
        const embedTagIframe = embedTag as HTMLIFrameElement
        // Set loading attributes: width, height and underlay background while it's loading
        embedTagIframe.setAttribute('width', this.attachmentWidth + 'px')
        embedTagIframe.setAttribute('height', this.realAttachmentHeight + 'px')
        embedTagIframe.style.background = this.attachmentColor

        // Override iframe for better behavior
        const currentSrc = embedTagIframe.getAttribute('src')
        let newSrc = addQueryParams(currentSrc, 'autoplay=1')

        if (!!this.linkData && LinkHelpers.isAudio(this.linkData)) {
          const fallbackImgSrc = fallbackImageUrl(FallbackAttachmentType.Audio, this.colorScheme, this.post.color)
          newSrc = addQueryParams(newSrc, `poster_url=${fallbackImgSrc}`)
        }

        // Need to set this for cross-origin autoplay to work, e.g. padlet.com/beethoven/play on starkindustries.padlet.org
        if (this.providerName.toLowerCase() === 'padlet drive') {
          embedTagIframe.setAttribute('allow', 'autoplay')
        }
        if (this.providerName.toLowerCase() === 'youtube') {
          newSrc = addQueryParams(newSrc, 'rel=0')
        }
        embedTagIframe.setAttribute('src', newSrc)
        this.embedHtml = embedTagIframe.outerHTML
      }
    })
  }

  zoom() {
    if (!this.linkData) return
    this.backToPreview()
    this.zoomPost({
      zoomedPostCid: this.post?.cid,
      zoomedLink: this.linkData,
    })
  }

  isUnsupportedAttachmentOnZoom(): boolean {
    // Zoom doesn't allow playing of some media types, so we open in browser
    return (
      device.zoomApp === true &&
      isAttachmentSupportedByZoom({
        contentCategory: this.contentCategory,
        providerName: this.providerName,
      })
    )
  }

  backToPreview() {
    this.unsetCurrentlyPlayingPost()
    // return $(this.$el).parents('.wish').removeClass('is-playing')
  }

  openInNew() {
    if (this.isApp) {
      this.openLink({
        linkType: 'external',
        url: this.urlForDisplay,
        contentCategory: this.contentCategory,
        contentSubcategory: this.contentSubcategory,
      })
    } else {
      window.open(this.urlForDisplay)
    }
  }

  @namespace('nativeApp').Action openLink!: (payload: { url; linkType; contentCategory; contentSubcategory }) => void
  playInApp() {
    let linkType = 'external'
    if (this.contentSubcategory === 'padlet' && this.isPadletAttachment) {
      linkType = 'padlet'
    } else if (this.contentCategory === 'photo' || this.contentCategory === 'file') {
      linkType = 'internal_mediaplayer'
    } else if (this.contentCategory === 'page' || this.contentCategory === 'document') {
      linkType = 'internal_webview'
    }

    this.openLink({
      url: this.urlForDisplay,
      linkType,
      contentCategory: this.contentCategory,
      contentSubcategory: this.contentSubcategory,
    })
  }

  @State isApp!: boolean
  @State isMac!: boolean
  @State canPostMessage!: boolean
  @State isPhone!: boolean

  play(e: MouseEvent): boolean {
    if (this.$el.closest('.surface-post')?.classList.contains('noclick')) {
      // Prevent play events when the post is being dragged
      e.preventDefault()
      return false
    }

    if ((this.isMac && e.metaKey) || (!this.isMac && e.ctrlKey)) {
      // Prevent play events when cmd/ctrl clicking so the default behavior
      // is used: open in new tab.
      return false
    }

    if (device.zoomApp && this.interaction === 'link') {
      // Prevent play events when in Zoom app
      e.preventDefault()
      openUrlInBrowserFromZoom(this.urlForOpening)
      return false
    }

    if (device.zoomApp && this.interaction === 'padlet') {
      // Open padlets within the same panel
      e.preventDefault()
      this.isZoomCollaborationGuest && getHostname(this.urlForOpening) === NATIVE_HOST
        ? openUrlInBrowserFromZoom(this.urlForOpening)
        : navigateTo(this.urlForOpening)
      return false
    }

    if (this.interaction === 'no-action') {
      return false
    }

    if (this.interaction === 'play') {
      // opening link: content is video and audio so we display player
      this.embedInline()
      if (this.contentCategory === 'video') {
        trackEvent('Attachment', 'Played video inline', null, null, { wishId: this.post.id })
      }
      if (this.contentCategory === 'audio') {
        trackEvent('Attachment', 'Played audio inline', null, null, { wishId: this.post.id })
      }
      e.preventDefault()
      return false
    }

    if (this.isApp && this.canPostMessage) {
      // Oct 2022: We don't open link anymore in app, but rely on the default behavior same as web
      // do nothing here
      if (!appCan('nativeExpandedPostV1')) {
        // opening link: isApp so use postMessage to open link
        this.playInApp()
        return false
      }
    }

    if (this.interaction === 'zoom') {
      // opening link: on phone, only images zoom
      // opening link: display preview for file type having the embed code
      this.zoom()
      e.preventDefault()
      return false
    }

    if (this.interaction === 'expand') {
      // expand post with text panel closed
      this.expandPost({ postCid: this.post.cid, xTextPanel: !this.isSmallerThanDesktop })
      e.preventDefault()
      trackEvent('Surface', 'Expanded post', null, null, { wishId: this.post.id })
      return false
    }

    if (this.isApp && this.canPostMessage) {
      // opening link priority moved to after expand: isApp so use postMessage to open link
      this.playInApp()
      return false
    }

    return true
  }

  /**
   * ZOOM
   */
  isUserInZoomCollaborationMode = false

  get isZoomCollaborationGuest(): boolean {
    return this.isUserInZoomCollaborationMode === true && this.isUserHostingZoomCollaboration === false
  }

  hasIntersectedViewport = false

  async mounted() {
    // 2022, Sep 22: observing intersection does not work for custom domain opened in an iframe in safari on mac/all browsers in iOS
    // => don't do it in those cases and just assume this component always intersects with the viewport. There'll be performance impact but it's better than things not loaded at all
    const isDisplayedInIframe = window.self !== window.top
    if (isDisplayedInIframe && (this.isSafari || this.isIOS)) {
      this.hasIntersectedViewport = true
      return
    }

    if (!this.hasIntersectedViewport) {
      const observeInitiated = observeIntersection(this.$el, () => {
        this.hasIntersectedViewport = true
      })
      if (!observeInitiated) {
        this.hasIntersectedViewport = true
      }
    }

    this.isUserInZoomCollaborationMode = await checkIfUserIsInZoomCollaborateMode()
  }
}
